import { getMic } from "@/lib/mic";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import { playUrl, stopAudio } from "@/lib/sharedAudio";
import VoiceCall from "@/components/VoiceCall";
import Navbar from "@/components/Navbar";
import TheField from "@/components/TheField";
import SimliAvatar from "@/components/SimliAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress as Bar } from "@/components/ui/progress";
import { Send, Mic, Square, Loader2, Award, ArrowLeft, Phone } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Coach() {
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [voice, setVoice] = useState("female");
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [rubric, setRubric] = useState(null);
  const [loadingR, setLoadingR] = useState(false);
  const scrollRef = useRef(null); const audioRef = useRef(null); const mrRef = useRef(null); const chunksRef = useRef([]);
  const avatarRef = useRef(null); const openingUrlRef = useRef(null); const premiumStartRef = useRef(null);
  const [premium, setPremium] = useState(false);
  const [callOpen, setCallOpen] = useState(false);

  useEffect(() => { api.get("/coach/scenarios").then((r) => setScenarios(r.data)).catch(() => {}); }, []);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, sending]);

  // Speak the opening when a drill starts (audio by default; live avatar only if premium is on).
  useEffect(() => {
    if (!session) return;
    speakReply(openingUrlRef.current);
    return () => { reportUsage(); try { avatarRef.current?.stop(); } catch {}; setPremium(false); premiumStartRef.current = null; };
    // eslint-disable-next-line
  }, [session?.id]);

  const reportUsage = async () => {
    if (!premiumStartRef.current) return;
    const secs = Math.round((Date.now() - premiumStartRef.current) / 1000);
    premiumStartRef.current = null;
    if (secs > 0) { try { await api.post("/usage/avatar", { seconds: secs }); } catch {} }
  };

  const togglePremium = async () => {
    if (!premium) {
      try {
        const { data } = await api.get("/simli/available");
        if (!data.available) { toast.error("The live face isn't available right now."); return; }
        if (!data.premium_available) { toast.error("You've used your premium live-face minutes this month.", { action: { label: "Upgrade", onClick: () => navigate("/upgrade") } }); return; }
      } catch { toast.error("Couldn't check your premium minutes."); return; }
      const ok = await avatarRef.current?.start();
      if (ok) { setPremium(true); premiumStartRef.current = Date.now(); }
      else toast.error("Couldn't start the live face.");
    } else {
      setPremium(false); await reportUsage();
      try { avatarRef.current?.stop(); } catch {}
    }
  };

  const rawPlay = (url) => { if (!url) return; playUrl(`${BACKEND}${url}`).catch(() => toast.error("Tap anywhere to enable her voice")); };

  // Lip-sync via the live avatar if connected, else fall back to plain audio.
  const speakReply = (url) => {
    if (!url) return;
    const full = `${BACKEND}${url}`;
    if (avatarRef.current?.isReady()) { avatarRef.current.speakUrl(full).catch(() => rawPlay(url)); }
    else rawPlay(url);
  };

  const start = async (sid) => {
    try { const { data } = await api.post("/coach/sessions", { scenario_id: sid, voice }); openingUrlRef.current = data.audio_url; setSession(data); setMessages(data.messages); }
    catch { toast.error("Could not start session."); }
  };

  const send = async (text) => {
    if (!text.trim() || sending) return;
    setInput(""); setMessages((m) => [...m, { role: "user", text }]); setSending(true);
    try {
      const { data } = await api.post(`/coach/sessions/${session.id}/message`, { text });
      setMessages((m) => [...m, ...(data.feedback ? [{ role: "feedback", text: data.feedback }] : []), { role: "coach", text: data.reply }]);
      speakReply(data.audio_url);
    } catch { toast.error("Try again."); } finally { setSending(false); }
  };

  const startRec = async () => {
    try {
      const stream = await getMic();
      const mr = new MediaRecorder(stream); mrRef.current = mr; chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => { stream.getTracks().forEach((t) => t.stop()); const blob = new Blob(chunksRef.current, { type: "audio/webm" }); const fd = new FormData(); fd.append("file", blob, "a.webm"); try { const { data } = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } }); if (data.text?.trim()) send(data.text.trim()); } catch { toast.error("Couldn't hear that."); } };
      mr.start(); setRecording(true);
    } catch { }
  };
  const stopRec = () => { mrRef.current?.stop(); setRecording(false); };

  const finish = async () => {
    if (messages.filter((m) => m.role === "user").length === 0) { toast.error("Have a conversation first."); return; }
    setLoadingR(true); stopAudio(); avatarRef.current?.clear();
    try { const { data } = await api.post(`/coach/sessions/${session.id}/rubric`); setRubric(data); }
    catch { toast.error("Could not score the session."); } finally { setLoadingR(false); }
  };

  if (!session) {
    return (
      <div className="min-h-screen bg-background"><Navbar />
        <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
          <p className="text-xs uppercase tracking-[0.2em] text-primary mb-2">AI Social Coach</p>
          <h1 className="font-serif text-4xl sm:text-5xl mb-3">Pick a scenario. I'll play the room.</h1>
          <p className="text-muted-foreground mb-6">I'll stay in character, coach you between turns, then score you at the end.</p>
          <div className="flex gap-2 mb-8">
            <span className="text-sm text-muted-foreground self-center mr-1">Coach voice:</span>
            {["female", "male"].map((v) => (
              <button key={v} onClick={() => setVoice(v)} data-testid={`voice-${v}`} className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${voice === v ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}>{v === "female" ? "Daisy" : "Michael"}</button>
            ))}
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {scenarios.map((s, i) => (
              <motion.button key={s.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                onClick={() => start(s.id)} data-testid={`coach-scenario-${s.id}`}
                className="group text-left rounded-2xl border border-border p-6 hover:border-primary/40 hover:-translate-y-1 transition-all duration-300">
                <span className="text-xs uppercase tracking-[0.15em] text-primary">{s.cat}</span>
                <h3 className="font-serif text-2xl mt-1">{s.title}</h3>
              </motion.button>
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col"><Navbar />
      <div className="max-w-2xl w-full mx-auto flex-1 flex flex-col px-4 sm:px-6 py-5">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => { setSession(null); setMessages([]); setRubric(null); }} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="coach-back"><ArrowLeft className="w-4 h-4" /> Scenarios</button>
          <span className="font-serif text-xl truncate px-2">{session.scenario_title}</span>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => { stopAudio(); setCallOpen(true); }} data-testid="coach-call-btn"><Phone className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Call</span></Button>
            <Button size="sm" className="rounded-full" onClick={finish} disabled={loadingR} data-testid="coach-finish-btn">{loadingR ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Award className="w-4 h-4 mr-1" /> Score me</>}</Button>
          </div>
        </div>
        <div className="flex flex-col items-center mb-4">
          <TheField intensity={0.7} size={148} active>
            <SimliAvatar ref={avatarRef} size={108} fallback={<img src="/adelaida/neutral.jpg" alt="Adelaida" className="w-full h-full object-cover" style={{ objectPosition: "center 22%" }} />} />
          </TheField>
          <button onClick={togglePremium} data-testid="coach-premium-toggle"
            className={`mt-3 text-xs px-3 py-1.5 rounded-full border transition-colors ${premium ? "border-primary/50 text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"}`}>
            {premium ? "● Live face on" : "Try the live face (premium)"}
          </button>
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-4 pb-4" data-testid="coach-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              {m.role === "feedback"
                ? <div className="mx-auto text-center text-xs text-primary bg-primary/10 rounded-full px-4 py-1.5" data-testid="coach-feedback">{m.text}</div>
                : <div className={`max-w-[82%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground rounded-br-sm" : "glass rounded-bl-sm"}`} data-testid={`coach-${m.role}`}>{m.text}</div>}
            </div>
          ))}
          {sending && <div className="flex justify-start"><div className="glass rounded-2xl px-4 py-3"><Loader2 className="w-4 h-4 animate-spin text-primary" /></div></div>}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex items-center gap-2 pt-3 border-t border-border">
          <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder={recording ? "Listening…" : "Your response…"} disabled={recording} data-testid="coach-input" className="rounded-full" />
          <Button type="button" size="icon" variant={recording ? "destructive" : "outline"} className="rounded-full shrink-0" onClick={recording ? stopRec : startRec} data-testid="coach-mic"> {recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}</Button>
          <Button type="submit" size="icon" className="rounded-full shrink-0" disabled={!input.trim() || sending} data-testid="coach-send"><Send className="w-4 h-4" /></Button>
        </form>
      </div>

      {callOpen && (
        <VoiceCall title={session.scenario_title} voice={voice === "male" ? "onyx" : "sage"}
          openingText={[...messages].reverse().find((m) => m.role === "coach")?.text || "Let's begin."}
          sessionKind="coach" canUseAvatar={true} autoAvatar={true}
          sendMessage={async (text) => {
            const { data } = await api.post(`/coach/sessions/${session.id}/message`, { text });
            if (data.feedback) setMessages((m) => [...m, { role: "feedback", text: data.feedback }]);
            return data.reply;
          }}
          onExchange={(m) => setMessages((prev) => [...prev, { role: m.role === "mirro" ? "coach" : "user", text: m.text }])}
          onEnd={() => setCallOpen(false)} />
      )}

      {rubric && (
        <div className="fixed inset-0 z-[60] bg-background/95 backdrop-blur-2xl flex items-center justify-center px-6 overflow-y-auto py-10" data-testid="coach-rubric">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full">
            <div className="flex justify-center mb-4"><TheField intensity={0.8} size={160} /></div>
            <p className="text-xs uppercase tracking-[0.2em] text-primary text-center mb-4">Your rubric</p>
            <div className="space-y-3 mb-6">
              {Object.entries(rubric.scores).map(([k, v]) => (
                <div key={k}><div className="flex justify-between text-sm mb-1 capitalize"><span className="text-muted-foreground">{k}</span><span>{v}</span></div><Bar value={v} className="h-1.5" /></div>
              ))}
            </div>
            <p className="font-serif text-xl text-center mb-6">{rubric.summary}</p>
            <div className="rounded-2xl border border-border p-5 mb-6">
              <div className="text-xs uppercase tracking-[0.18em] text-primary mb-2">Homework</div>
              <ul className="space-y-2">{rubric.homework.map((h, i) => <li key={i} className="text-sm text-muted-foreground flex gap-2"><span className="text-primary">{i + 1}</span>{h}</li>)}</ul>
            </div>
            <Button className="w-full rounded-full" onClick={() => { setSession(null); setMessages([]); setRubric(null); }} data-testid="coach-rubric-done">Done</Button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
